import {
  withResponsiveOptions,
  withBreakpointObserver,
  getClosestParent,
} from '@studiometa/js-toolkit';
import { trapFocus, untrapFocus } from '@studiometa/js-toolkit/utils';
import { MenuBtn, MenuList, Menu as MenuCore } from '@studiometa/ui';
import MenuItemCollection from './MenuItemCollection.js';

/**
 * @param {*} childInstance
 * @param {*} parentInstance
 * @returns {boolean}
 */
function fixedIsDirectChild(childInstance, parentInstance) {
  return getClosestParent(childInstance, parentInstance.constructor) === parentInstance;
}

/**
 * Menu class.
 */
export default class Menu extends withResponsiveOptions(withBreakpointObserver(MenuCore)) {
  static config = {
    ...MenuCore.config,
    emits: ['open', 'close'],
    refs: ['wrapper[]', 'videoWrapper[]'],
    options: {
      ...MenuCore.config.options,
      mode: {
        type: String,
        default: 'click',
        responsive: true,
      },
    },
    components: {
      MenuList,
      MenuBtn,
      MenuItemCollection,
      Menu,
    },
  };

  /**
   * @returns {boolean}
   */
  get isFirstLevel() {
    return getClosestParent(this, Menu) === null;
  }

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return this.menuList?.isOpen ?? false;
  }

  /**
   * @param {import('@studiometa/js-toolkit').KeyServiceProps} props
   */
  keyed({ event, isDown, isUp, ESC }) {
    if (!this.isOpen || !this.isFirstLevel) {
      return;
    }

    if (isDown) {
      trapFocus(this.menuList.$el, event);
    }

    if (ESC && isUp) {
      this.close();
    }
  }

  /**
   * @param {*} props
   */
  onMenuListItemsOpen(props) {
    if (fixedIsDirectChild(props.target, this)) {
      super.onMenuListItemsOpen(props);
      this.$emit('open');
    }
  }

  /**
   * @param {object} props
   * @param {HTMLElement} props.target
   */
  onMenuListItemsClose({ target }) {
    if (fixedIsDirectChild(target, this)) {
      if (this.isFirstLevel) {
        untrapFocus();
      }
      this.$emit('close');

      this.$refs.wrapper?.forEach((wrapper) => {
        wrapper.classList.remove('is-visible');
      });
      this.$refs.videoWrapper?.forEach((videoWrapper) => {
        videoWrapper.classList.remove('is-visible');
      });
    }
  }

  /**
   * @param {object} props
   * @param {Event} props.event
   * @param {HTMLElement} props.target
   */
  onMenuBtnClick({ target, event }) {
    if (fixedIsDirectChild(target, this) && this.shouldReactOnClick) {
      event.preventDefault();
      this.toggle();
    }
  }

  /**
   * onMenuItemCollectionMouseenter
   * @param {object} props
   * @param {HTMLElement} props.target
   */
  onMenuItemCollectionMouseenter({ target }) {
    const image = target.$options.image;
    if (!image) {
      return;
    }

    this.$children.MenuItemCollection?.forEach((item) => {
      if (item === target) {
        return;
      }

      this.$children.Menu?.forEach((menu) => {
        if (item.$el.contains(menu.$el) && menu.isOpen) {
          menu.close();
        }
      });
    });

    this.$refs.wrapper?.forEach((wrapper) => {
      wrapper.classList.toggle('is-visible', wrapper.dataset.optionImage === image);
    });
    this.$refs.videoWrapper?.forEach((videoWrapper) => {
      videoWrapper.classList.toggle('is-visible', videoWrapper.dataset.optionImage === image);
    });
  }

  /**
   * onMenuItemCollectionMouseleave
   */
  onMenuItemCollectionMouseleave() {
    const hasOpenLvl3 = this.$el.querySelector('.submenu-lvl3-is-open');
    if (hasOpenLvl3) {
      return;
    }

    this.$refs.wrapper?.forEach((wrapper) => {
      wrapper.classList.remove('is-visible');
    });
    this.$refs.videoWrapper?.forEach((videoWrapper) => {
      videoWrapper.classList.remove('is-visible');
    });
  }
}
